import isEqual from 'lodash/isEqual';
import Vue, { PropType } from 'vue';
import { Candidate, WorkAuthorization } from '~/services/CandidateService';
import { User } from '~/services/IdentityService';
import { CandidateJobBuilder } from './CandidateJobBuilder';
import { ExcludeId, Operation, WorkAuthorizationOperation } from './CandidateJobBuilder.types';

export interface ObjectWithId {
    id: string;
    [key: string]: any;
}

type ObjectWithKeys<T extends ObjectWithId, K extends ExcludeId<T>> = { id: string } & Pick<T, K>;

export const TEMP_ID_PREFIX = 'temp__';
export const TEMP_ID_VALUE_DELIMITER = '__::__';
export const TEMP_VERIFICATION_PROGRAM_ID_PREFIX = 'temp_verification__';
export const COMMITTED_VERIFICATION_PROGRAM_ID_PREFIX = 'committed_verification__';

function extract<T extends ObjectWithId, K extends ExcludeId<T>>(
    object: T,
    ...keys: K[]
): ObjectWithKeys<T, K>;
function extract<T extends ObjectWithId, K extends ExcludeId<T>>(
    object: T[],
    ...keys: K[]
): ObjectWithKeys<T, K>[];
function extract<T extends object, K extends keyof T>(object: T, ...keys: K[]): Pick<T, K>;
function extract<T extends ObjectWithId, K extends ExcludeId<T>>(object: T | T[], ...keys: K[]) {
    if (Array.isArray(object)) return object.map(o => extract(o, ...keys));
    const initial = Object.prototype.hasOwnProperty.call(object, 'id') ? { id: object.id } : {};
    return keys.reduce((acc, key) => ({ ...acc, [key]: object[key] }), initial as T);
}

export { extract };

type OperationType<O> = O extends Operation<infer T> ? T : never;

export function createListOperations<O extends Operation<any>, T extends ObjectWithId>(
    current: T[],
    original: T[],
    ...keys: ExcludeId<T>[]
): Operation<OperationType<O>>[] {
    const extractValues = (item: T) =>
        keys.reduce((acc, key) => ({ ...acc, [key]: item[key] }), {});

    const operations: Operation<any>[] = [];

    // Find deleted items
    const deleted = original.filter(
        wa =>
            !CandidateJobBuilder.isTempVerificationProgramItem(wa) &&
            !current.some(cwa => cwa.id === wa.id)
    );
    for (const wa of deleted)
        operations.push({
            op: 'delete',
            value: { id: wa.id },
        });

    // Find new items
    const created = current.filter(
        cwa =>
            CandidateJobBuilder.isTempItem(cwa) ||
            CandidateJobBuilder.isCommittedVerificationProgramItem(cwa)
    );
    for (const wa of created)
        operations.push({
            op: 'create',
            value: extractValues(wa),
        });

    // Find updated items
    const updated = current.filter(cwa => {
        if (
            CandidateJobBuilder.isTempItem(cwa) ||
            CandidateJobBuilder.isTempVerificationProgramItem(cwa) ||
            CandidateJobBuilder.isCommittedVerificationProgramItem(cwa)
        ) {
            return false;
        }

        const originalWA = original.find(wa => wa.id === cwa.id);
        return !!originalWA && !isEqual(originalWA, cwa);
    });
    for (const wa of updated)
        operations.push({
            op: 'update',
            value: { id: wa.id, ...extractValues(wa) },
        });

    return operations;
}

type CandidateModal = Vue & {
    builder: CandidateJobBuilder | null;
    user: User;
    candidate: Candidate;
    workAuthorizations: WorkAuthorization[];
};

export function commonProps() {
    return {
        user: {
            type: Object as PropType<User>,
            required: true,
        },
        candidate: {
            type: Object as PropType<Candidate>,
            required: true,
        },
        workAuthorizations: {
            type: Array as PropType<WorkAuthorization[]>,
            required: true,
        },
    };
}

export function setupBuilder(this: CandidateModal) {
    this.builder = new CandidateJobBuilder({
        user: this.user,
        candidate: this.candidate,
        workAuthorizations: this.workAuthorizations,
    });

    const reinitBuilder = () => {
        if (!this.builder) return;
        this.builder.init(
            {
                user: this.user,
                candidate: this.candidate,
                workAuthorizations: this.workAuthorizations,
            },
            true
        );
    };

    this.$watch('user', () => reinitBuilder(), { deep: true });
    this.$watch('candidate', () => reinitBuilder(), { deep: true });
    this.$watch('workAuthorizations', () => reinitBuilder(), { deep: true });

    return this.builder;
}
